import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  doc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Grid,
  Paper,
  Avatar,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import EditPost from "./EditPost";
import Comments from "./Comments";
import { db } from "../firebase";

const ref = collection(db, "posts");

const Posts = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showCommentsId, setShowCommentsId] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    const q = query(ref, orderBy("createdAt", "desc"));
    const unsubscribeDb = onSnapshot(q, (snapshot) => {
      const posts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setData(posts);
      setIsLoading(false);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeDb();
    };
  }, []);

  const handleEditSave = async (post, editText) => {
    if (post && editText) {
      const postRef = doc(db, "posts", post.id);
      await updateDoc(postRef, {
        body: editText,
      });
      setEditingPost(null);
    }
  };

  const handleDelete = async (id) => {
    setDeleteConfirmation(id);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setShowDeleteAlert(false);
  };

  const confirmDelete = async () => {
    if (deleteConfirmation) {
      const postRef = doc(db, "posts", deleteConfirmation);
      await deleteDoc(postRef);
      setDeleteConfirmation(null);
    }
    setShowDeleteAlert(true);
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  if (isLoading) {
    return <isLoading />;
  }

  return (
    <div className="p-4 space-y-8">
      <h1 className="text-4xl text-white font-bold text-center mb-8">Posts</h1>
      <Grid container spacing={5}>
        {data.map((post) => (
          <Grid item xs={12} sm={6} key={post.id}>
            <Paper
              elevation={3}
              className="p-6 hover:scale-105 transition-transform duration-200"
            >
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Avatar
                    src={
                      post.authorPhoto ||
                      "https://w1.pngwing.com/pngs/386/684/png-transparent-face-icon-user-icon-design-user-profile-share-icon-avatar-black-and-white-silhouette.png"
                    }
                    alt="User"
                    sx={{ width: 48, height: 48 }}
                  />
                </Grid>
                <Grid item xs={10}>
                  <div className="mb-2">
                    <Typography variant="subtitle1" inline>
                      {post.authorName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" inline>
                      {" "}
                      ·{" "}
                      {new Date(post.createdAt).toLocaleDateString("tr-TR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Typography>
                  </div>

                  {editingPost?.id === post.id ? (
                    <EditPost
                      post={post}
                      onSave={handleEditSave}
                      onCancel={() => setEditingPost(null)}
                    />
                  ) : (
                    <div className="space-y-4">
                      {post.photoUrl && (
                        <img
                          src={post.photoUrl}
                          alt={post.body}
                          className="my-4 w-full h-64 object-contain rounded-md"
                        />
                      )}
                      <Typography variant="body1">{post.body}</Typography>

                      {currentUser &&
                        currentUser.displayName === post.authorName && (
                          <div className="flex justify-start space-x-3 mt-4">
                            <Button
                              onClick={() => setEditingPost(post)}
                              variant="text"
                              color="primary"
                              style={{ border: "1px solid #3b82f6" }}
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDelete(post.id)}
                              variant="text"
                              color="secondary"
                              style={{ border: "1px solid #ef4444" }}
                            >
                              Delete
                            </Button>
                          </div>
                        )}

                      <Button
                        onClick={() =>
                          setShowCommentsId(
                            showCommentsId === post.id ? null : post.id
                          )
                        }
                        variant="outlined"
                        color="primary"
                        fullWidth
                      >
                        {showCommentsId === post.id
                          ? "Hide Comments"
                          : "Show Comments"}
                      </Button>

                      {showCommentsId === post.id && (
                        <div className="mt-4 p-4 border border-gray-200 rounded shadow">
                          <Comments postId={post.id} />
                        </div>
                      )}
                    </div>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {deleteConfirmation && (
        <Dialog
          open={Boolean(deleteConfirmation)}
          onClose={cancelDelete}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Do you want to delete this content?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete} color="primary" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color="primary"
              variant="contained"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Snackbar
        open={showDeleteAlert}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Alert severity="warning">Post deleted successfully!</Alert>
      </Snackbar>
    </div>
  );
};

export default Posts;
